import { z, ZodError } from 'zod'
import { oocApi } from '@/lib/apiClient'
import { oocUrls } from '@/lib/urlBuilder'
import { normalizeZodError } from '@/lib/apiErrors'

// GET /online-ordering/mars/api/v3/:ref/availability/time-slots?orderType=:id
// Note: ref is placed directly under the version prefix, not under /stores/ — intentional per OOC API design

export const StoreClosureEntrySchema = z.object({
	message: z.string(),
	date: z.string(),
})
export type StoreClosureEntry = z.infer<typeof StoreClosureEntrySchema>

export const AsapTimeSlotSchema = z.object({
	expectedTime: z.string(),
	orderTime: z.string(),
	isAvailable: z.boolean(),
})
export type AsapTimeSlot = z.infer<typeof AsapTimeSlotSchema>

export const FutureTimeSlotSchema = z.object({
	expectedTime: z.string(),
	orderTime: z.string(),
	isAvailable: z.boolean(),
	isOvernight: z.boolean(),
	applicableDate: z.string(),
})
export type FutureTimeSlot = z.infer<typeof FutureTimeSlotSchema>

export const TimeSlotsResponseSchema = z.object({
	storeClosures: z.array(StoreClosureEntrySchema),
	// API omits asapTimeSlot entirely when ASAP is unavailable — nullish() accepts absent or null
	asapTimeSlot: AsapTimeSlotSchema.nullish(),
	futureTimeSlots: z.array(FutureTimeSlotSchema),
})
export type TimeSlotsResponse = z.infer<typeof TimeSlotsResponseSchema>

export const ScheduleSchema = z.object({
	ref: z.string(),
	name: z.string(),
})
export type Schedule = z.infer<typeof ScheduleSchema>

// API returns a bare array — wrap to a consistent shape for callers
export const SchedulesResponseSchema = z
	.array(ScheduleSchema)
	.transform((schedules) => ({ schedules }))
export type SchedulesResponse = { schedules: Schedule[] }

export function getTimeSlots(ref: string, orderTypeId?: string): Promise<TimeSlotsResponse> {
	const config = orderTypeId ? { params: { orderType: orderTypeId } } : undefined
	return oocApi
		.get<unknown>(oocUrls.timeSlots(ref), config)
		.then((raw) => TimeSlotsResponseSchema.parse(raw))
		.catch((err: unknown) => {
			if (err instanceof ZodError) throw normalizeZodError(err)
			throw err
		})
}

export function getSchedules(
	ref: string,
	orderTypeId: string,
	orderTimeUtc: string,
): Promise<SchedulesResponse> {
	return oocApi
		.get<unknown>(oocUrls.schedules(ref), {
			params: { orderType: orderTypeId, time: orderTimeUtc },
		})
		.then((raw) => SchedulesResponseSchema.parse(raw))
		.catch((err: unknown) => {
			if (err instanceof ZodError) throw normalizeZodError(err)
			throw err
		})
}
